<template>
  <section class="evidence-category-card">
    <div class="evidence-category-card__header">
      <h3 class="evidence-category-card__title">{{ $t(categoryTitleToken(category)) }}</h3>

      <div v-if="showReviewActions" class="evidence-category-card__review-actions">
        <button
            type="button"
            class="evidence-category-card__collapse"
            :aria-label="isCollapsed ? $t('app.assessment_mode.text.input.expand_section') : $t('app.assessment_mode.text.input.collapse_section')"
            :aria-expanded="String(!isCollapsed)"
            @click="toggleCollapse"
        >
          <v-icon small class="evidence-category-card__collapse-icon" :class="{ 'evidence-category-card__collapse-icon--collapsed': isCollapsed }">
            {{ mdiChevronDown }}
          </v-icon>
        </button>
      </div>
    </div>

    <div v-show="!isCollapsed" class="evidence-category-card__body">
      <PatientIdField
          v-if="isPatientCategory && !readOnly"
          :input-id="patientIdInputId"
          :value="patientId"
          @input="handlePatientIdInput"
      />

      <template v-if="isClinicalObservationsCategory && !canEditItems">
        <ClinicalObservationsList
            v-if="hasClinicalObservations"
            :positive-observations="positiveObservations"
            :negative-observations="negativeObservations"
            :unknown-observations="unknownObservations"
        />
        <p v-else class="evidence-category-card__empty">
          —
        </p>
      </template>

      <template v-else>
        <p v-if="displayItems.length === 0" class="evidence-category-card__empty">
          —
        </p>

        <div
            v-else-if="isClinicalObservationsCategory && canEditItems"
            class="evidence-observations evidence-category-card__items"
        >
          <div
              v-for="group in editObservationGroups"
              :key="group.id"
              class="evidence-observations__group"
          >
            <div
                class="evidence-observations__heading"
                :class="`evidence-observations__heading--${group.id}`"
            >
              <span
                  class="evidence-observations__dot"
                  :class="`evidence-observations__dot--${group.id}`"
              />
              {{ $t(group.labelToken) }}
            </div>

            <EvidenceCategoryItem
                v-for="item in group.items"
                :key="item.id"
                :item="item"
                :can-edit-items="canEditItems"
                :read-only="readOnly"
                :editing="editingItemId === item.id"
                :editing-value="editingValue"
                :display-value="displayValue(item)"
                :original-value="originalValue(item.id)"
                :observation-status="currentObservationStatus(item)"
                :status-label-token="statusLabelToken(currentObservationStatus(item))"
                :drafted="draftStore.isDrafted(item.id)"
                :marked-incorrect="draftStore.isMarkedIncorrect(item.id)"
                :committed-incorrect="draftStore.isCommittedIncorrect(item.id)"
                @edit-input="handleEditInput"
                @save="saveEdit"
                @cancel="cancelEdit"
                @value-change="handleValueChange"
                @remove="handleRemove"
                @validation-change="handleValidationChange"
                @start-edit="startEdit"
                @revert="revert"
            />
          </div>
        </div>

        <div v-else class="evidence-category-card__items">
          <EvidenceCategoryItem
              v-for="item in displayItems"
              :key="item.id"
              :item="item"
              :can-edit-items="canEditItems"
              :read-only="readOnly"
              :editing="editingItemId === item.id"
              :editing-value="editingValue"
              :display-value="displayValue(item)"
              :original-value="originalValue(item.id)"
              :observation-status="currentObservationStatus(item)"
              :status-label-token="statusLabelToken(currentObservationStatus(item))"
              :drafted="draftStore.isDrafted(item.id)"
              :marked-incorrect="draftStore.isMarkedIncorrect(item.id)"
              :committed-incorrect="draftStore.isCommittedIncorrect(item.id)"
              @edit-input="handleEditInput"
              @save="saveEdit"
              @cancel="cancelEdit"
              @value-change="handleValueChange"
              @remove="handleRemove"
              @validation-change="handleValidationChange"
              @start-edit="startEdit"
              @revert="revert"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from 'vue';
import { mdiChevronDown } from '@mdi/js';
import type { DraftValidationError, EvidenceCategory, EvidenceItem, ObservationStatus } from '../../types/text-mode.types';
import { useDraftStore } from '../../stores/draft.store';
import { usePatientStore } from '../../stores/patient.store';
import PatientIdField from './PatientIdField.vue';
import ClinicalObservationsList from './ClinicalObservationsList.vue';
import EvidenceCategoryItem from './EvidenceCategoryItem.vue';
import { OBSERVATION_STATUS_LABEL_TOKENS } from '../../constants/observation-status.constants';
import { buildClinicalObservationDisplayModel } from '../../utils/clinical-observation-display';

const CATEGORY_TITLE_TOKENS: Record<string, string> = {
  patient: 'app.assessment_mode.text.input.patient_header',
  vitals: 'app.assessment_mode.text.input.vitals_header',
  observations: 'app.assessment_mode.text.input.clinical_observations_header',
  'clinical-observations': 'app.assessment_mode.text.input.clinical_observations_header',
  'follow-up-questions': 'app.assessment_mode.text.input.follow_up_questions_header',
};

export default defineComponent({
  name: 'EvidenceCategoryCard',

  components: {
    PatientIdField,
    ClinicalObservationsList,
    EvidenceCategoryItem,
  },

  props: {
    category: {
      type: Object as PropType<EvidenceCategory>,
      required: true,
    },
    canEditItems: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const draftStore = useDraftStore();
    const patientStore = usePatientStore();

    const editingItemId = ref<string | null>(null);
    const editingValue  = ref('');
    const isCollapsed = ref(false);
    const patientId = computed(() => patientStore.patient?.id ?? '');

    const isPatientCategory = computed(
      () => props.category.id.toLowerCase() === 'patient',
    );

    const isVitalsCategory = computed(
      () => props.category.id.toLowerCase() === 'vitals',
    );

    const showReviewActions = computed(
      () => props.readOnly && (isPatientCategory.value || isVitalsCategory.value),
    );

    const patientIdInputId = computed(
      () => `evidence-patient-id-${props.category.id || 'patient'}`,
    );

    /** Display value: draft override > committed value > item.value (evidence store) */
    const displayValue = (item: EvidenceItem): string | number | boolean | null => {
      const override = draftStore.draftOverrides[item.id];
      if (override && override.draftValue !== null && override.draftValue !== undefined) {
        return override.draftValue;
      }
      // After Submit RHS, read from committed values
      const committedVal = draftStore.committed.values[item.id];
      if (committedVal !== undefined) return committedVal;
      return item.value;
    };

    /** Original transcript value */
    const originalValue = (itemId: string): string | number | boolean | null =>
      draftStore.originalExtraction[itemId] ?? null;

    /** Effective observation status: draft override > committed > item default */
    const currentObservationStatus = (item: EvidenceItem): ObservationStatus | null => {
      const override = draftStore.draftOverrides[item.id];
      if (override?.observationStatus) return override.observationStatus;
      // After Submit RHS, draftOverrides is cleared; read committed status
      const committed = draftStore.committed.observationStatuses[item.id];
      if (committed) return committed;
      return item.observationStatus ?? null;
    };

    const isClinicalObservationsCategory = computed(
      () => props.category.id === 'clinical-observations',
    );

    const clinicalObservationDisplay = computed(
      () => buildClinicalObservationDisplayModel(props.category.items, currentObservationStatus),
    );

    const clinicalObservationDisplayItems = computed(
      () => clinicalObservationDisplay.value.items,
    );

    const positiveObservations = computed(
      () => clinicalObservationDisplay.value.positiveObservations,
    );

    const negativeObservations = computed(
      () => clinicalObservationDisplay.value.negativeObservations,
    );

    const unknownObservations = computed(
      () => clinicalObservationDisplay.value.unknownObservations,
    );

    const displayItems = computed(
      () => {
        if (!isClinicalObservationsCategory.value) {
          return props.category.items;
        }

        return clinicalObservationDisplayItems.value;
      },
    );

    const editObservationGroups = computed(
      () => clinicalObservationDisplay.value.editGroups,
    );

    const hasClinicalObservations = computed(
      () => clinicalObservationDisplay.value.hasItems,
    );

    const statusLabelToken = (s: ObservationStatus | null): string =>
      s ? OBSERVATION_STATUS_LABEL_TOKENS[s] : '';

    const categoryTitleToken = (category: EvidenceCategory): string => {
      return CATEGORY_TITLE_TOKENS[category.id.toLowerCase()] || category.title;
    };

    const handlePatientIdInput = (value: string): void => {
      patientStore.setPatientId(value);
    };

    const handleEditInput = (value: string): void => {
      editingValue.value = value;
    };

    const startEdit = (itemId: string, currentValue: string): void => {
      editingItemId.value = itemId;
      editingValue.value  = currentValue;
    };

    const saveEdit = (itemId: string): void => {
      draftStore.updateDraftField(itemId, editingValue.value);
      // NOTE: do NOT emit 'update-item' here – the evidence store is only
      // synced after the user presses Submit RHS (commitDraftToEvidenceChanges).
      // Emitting here would corrupt item.value and break cancel/revert.
      editingItemId.value = null;
      editingValue.value  = '';
    };

    const cancelEdit = (): void => {
      editingItemId.value = null;
      editingValue.value  = '';
    };

    const revert = (itemId: string): void => {
      draftStore.revertDraftField(itemId);
      emit('revert-item', { itemId });
    };

    const handleValueChange = (payload: { itemId: string; value: string | number | boolean | null }): void => {
      draftStore.updateDraftField(payload.itemId, payload.value);
    };

    const handleRemove = (itemId: string): void => {
      draftStore.removeDraftObservation(itemId);
    };

    const handleValidationChange = (payload: { itemId: string; error: DraftValidationError | null }): void => {
      draftStore.setDraftValidationError(payload.itemId, payload.error);
    };

    const toggleCollapse = (): void => {
      isCollapsed.value = !isCollapsed.value;
    };

    return {
      mdiChevronDown,
      draftStore,
      patientId,
      editingItemId,
      editingValue,
      isCollapsed,
      isPatientCategory,
      isVitalsCategory,
      showReviewActions,
      isClinicalObservationsCategory,
      positiveObservations,
      negativeObservations,
      unknownObservations,
      displayItems,
      editObservationGroups,
      hasClinicalObservations,
      patientIdInputId,
      displayValue,
      originalValue,
      currentObservationStatus,
      statusLabelToken,
      categoryTitleToken,
      handlePatientIdInput,
      handleEditInput,
      startEdit,
      saveEdit,
      cancelEdit,
      revert,
      handleValueChange,
      handleRemove,
      handleValidationChange,
      toggleCollapse,
    };
  },
});
</script>
